// packages/web/src/utils/installDateUtils.ts
// Centralized install date calculation utility - single source of truth

export interface InstallDateResult {
  installDays: number | null;
  installDate: Date | null;
  formattedInstallDate: string;
}

export interface ServiceInstallData {
  // Primary fields from marketplace_services table
  expected_install_time_days?: number;
  validation_days_estimate?: number;
  expected_contract_hours?: number;
}

/**
  * Calculate install days from service data
  * Single source of truth for install date calculations across the application
  * Uses expected_install_time_days + validation_days_estimate from marketplace_services
  */
export function calculateInstallDays(service: ServiceInstallData): number | null {
  // Primary calculation using marketplace_services fields
  const installationDays = service.expected_install_time_days || 0;
  const validationDays = service.validation_days_estimate || 0;

  // Sum installation and validation days, round up to nearest day
  const totalDays = installationDays + validationDays;
  const roundedDays = Math.ceil(totalDays);

  return roundedDays > 0 ? roundedDays : null;
}

/**
 * Calculate and format install date from service data
 * Returns both raw data and formatted display string
 */
export function calculateInstallDate(service: ServiceInstallData): InstallDateResult {
  const installDays = calculateInstallDays(service);

  // Calculate install date if we have install days
  const installDate = installDays ? new Date(Date.now() + (installDays * 24 * 60 * 60 * 1000)) : null;

  // Format install date consistently for both server and client
  const formattedInstallDate = installDate ? (() => {
    const month = installDate.toLocaleString('en-US', { month: 'short' });
    const day = installDate.getDate();
    const year = installDate.getFullYear();
    return `${month} ${day}, ${year}`;
  })() : 'TBD';

  return {
    installDays,
    installDate,
    formattedInstallDate
  };
}